import Tooltip from 'components/tooltip/Tooltip';
import IdmTablePaging from './IdmTablePaging';

export type TableElement = {};

export type TableColumnAlign = 'left' | 'center' | 'right';

export type TableColumn<T extends TableElement> = {
  title: string;
  html: (e: T) => JSX.Element;
  width?: number;
  /** Cell text alignment; 'center' when left out, as every older page expects. */
  align?: TableColumnAlign;
  /** Shown on hovering the header, for a column whose title needs explaining. */
  tooltip?: string;
};

export type TableModel<T extends TableElement> = {
  columns: TableColumn<T>[];
};

export type Props<T extends TableElement> = {
  model: TableModel<T>;
  elements: T[];
  pageNumber: number;
  totalElements: number;
  pageChanged: (pageNumber: number) => void;
  onRowClick?: (element: T) => void;
  rowClassName?: (element: T) => string;
  /**
   * Stable identity of a row, e.g. `(row) => row.id`. Without it the row index
   * is used, which is enough for a read-only table but remounts every cell when
   * the rows move; a table with an input in a cell must pass it, or the input
   * loses focus on every re-render.
   */
  rowKey?: (element: T, index: number) => string | number;
  /**
   * Framed look: 1px border around the table, 1px row borders, no right
   * margin and left-aligned headers. Off by default.
   */
  framed?: boolean;
  /**
   * Renders the paging footer. On by default; pass `false` for a table that
   * shows everything it has, so no lone page button is drawn.
   */
  showPaging?: boolean;
};

export type PagingData = {
  offset: number;
  limit: number;
};

const alignClasses: Record<TableColumnAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export const GenericTable = <T extends TableElement>({
  model,
  elements,
  pageNumber,
  totalElements,
  pageChanged,
  onRowClick,
  rowClassName,
  rowKey,
  framed,
  showPaging,
}: Props<T>) => {
  const totalPages = totalElements;
  const sumWidth = model.columns.reduce(
    (sum, column) => sum + (column.width || 1),
    0,
  );
  const widthOf = (c: TableColumn<T>) => ((c.width || 1) / sumWidth) * 100;
  const headerAlignOf = (c: TableColumn<T>) =>
    alignClasses[c.align ?? (framed ? 'left' : 'center')];
  const cellAlignOf = (c: TableColumn<T>) => alignClasses[c.align ?? 'center'];

  return (
    <div
      className={
        framed
          ? 'flex flex-grow flex-col overflow-hidden rounded-sm border border-design-secondary-black'
          : 'mr-6 flex flex-grow flex-col'
      }
    >
      <div className="bg-darker-red">
        <div className="flex">
          {model.columns.map((c, columnIndex) => (
            <div
              key={c.title || columnIndex}
              className={`p-3 ${headerAlignOf(c)}`}
              style={{ width: `${widthOf(c)}%` }}
            >
              {c.tooltip ? (
                <Tooltip text={c.tooltip}>
                  <span className="cursor-help font-semibold text-white underline decoration-dotted">
                    {c.title}
                  </span>
                </Tooltip>
              ) : (
                <span className="font-semibold text-white">{c.title}</span>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="flex-grow">
        {elements.length > 0 ? (
          elements.map((e, index) => (
            <div
              key={rowKey ? rowKey(e, index) : index}
              className={`flex ${
                framed
                  ? 'border-b border-white-contrast dark:border-design-secondary-black'
                  : 'border-b-2'
              } border-solid last:border-b-0 hover:bg-light-gray dark:hover:bg-design-secondary-black ${
                onRowClick ? 'cursor-pointer' : ''
              } ${rowClassName ? rowClassName(e) : ''}`}
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(e) : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onRowClick(e);
                      }
                    }
                  : undefined
              }
            >
              {model.columns.map((c, columnIndex) => (
                <div
                  key={c.title || columnIndex}
                  className={`p-2 ${cellAlignOf(c)}`}
                  style={{ width: `${widthOf(c)}%` }}
                >
                  {c.html(e)}
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className="flex h-32 items-center justify-center bg-light-gray dark:bg-design-secondary-black">
            <div>No data to display</div>
          </div>
        )}
      </div>
      {elements.length > 0 && showPaging !== false && (
        <IdmTablePaging
          page={pageNumber}
          totalPages={totalPages}
          handlePagination={pageChanged}
        />
      )}
    </div>
  );
};
